import { Button, createTheme, Skeleton, Stack, ThemeProvider, Typography } from "@mui/material";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import NewMgmtModal from "./Modal/NewMgmtModal";
import { useState } from "react";
import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import { useTranslation } from "react-i18next";
import { tableOptions } from "../TableHelpers/options";
import AttToken from "../../helpers/attToken";
import Manages from "../../service/Manages";
import Users from "../../service/Users";
import Teams from "../../service/Teams";
import ServiceTypes from "../../service/ServiceTypes";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import QueryFn from "../../helpers/QueryFn";
import EditIcon from '@mui/icons-material/Edit';

export type manageType = {
  dataAlteracao?: number,
  dataCriacao?: number,
  dataDelecao?: number,
  empresa: string,
  equipes?: { id: string, nome: string }[],
  gerentes?: string[],
  id?: string,
  nome?: string,
  status: boolean,
  tiposDeServico?: { id: string, nome: string, conjuntosAssociados: string[] }[],
  geral?: boolean
};

const managesService = new Manages();
const userService = new Users();
const teamService = new Teams();
const serviceTypeSerice = new ServiceTypes();
const queryFn = new QueryFn();

export default function NewManages() {
  const [open, setOpen] = useState<boolean>(false);
  const [showManages, setShowManages] = useState<{ open: boolean, id: string }>({ open: false, id: '' });
  const [showTeams, setShowTeams] = useState<{ open: boolean, id: string }>({ open: false, id: '' });
  const [showServices, setShowServices] = useState<{ open: boolean, id: string }>({ open: false, id: '' });
  const [selectedManage, setSelectedManage] = useState<manageType | null>(null);
  const { t } = useTranslation('translation');

  const results = queryFn.useFetchMultiple([
    {
      key: 'GET_MANAGESv2',
      fn: async () => {
        const token = await AttToken();
        if (token) {
          const response = await managesService.getAll(token);
          return response;
        }
      },
    },
    {
      key: 'GET_USERSV2',
      fn: async () => {
        const token = await AttToken();
        if (token) {
          const response = await userService.getAllUsers(token);
          return response;
        }
      },
    },
    {
      key: 'GET_TEAMSV2',
      fn: async () => {
        const token = await AttToken();
        if (token) {
          const response = await teamService.getSimpleTeams(token);
          return response;
        }
      },
    },
    {
      key: 'GET_SERVICESV2',
      fn: async () => {
        const token = await AttToken();
        if (token) {
          const body = { ...token, empresa: token.userEmpresa };
          const response = await serviceTypeSerice.getAllServices(body);
          return response;
        }
      },
    }
  ],
  );

  const isLoading = results?.some((result: any) => result.isLoading);
  const isFetching = results?.some((result: any) => result.isFetching);

  const columns = [
    {
      name: "nome",
      label: t('name'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: 'dataCriacao',
      label: t('Managements.createdAt'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          const data = new Date(value).toLocaleString("pt-br");
          return data;
        },
      }
    },
    {
      name: 'status',
      label: t('status'),
      options: {
        customBodyRender: (value: boolean) => {
          return (
            <>
              {
                !value
                  ? <span className='tag is-danger'>{`${t('inactive')}`}</span>
                  : <span className='tag is-primary'>{t('active')}</span>
              }
            </>);
        },
        filter: false,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: 'gerentes',
      label: 'Gerentes',
      options: {
        customBodyRender: (value: any[], row: any) => {
          return (
            <>
              {value && <>
                {showManages.id !== row.rowIndex
                  ? <Button
                    disabled={isLoading || isFetching}
                    onClick={() => setShowManages({ open: true, id: row.rowIndex })}
                    className="button is-light"
                    endIcon={<RemoveRedEyeIcon />}
                  >
                    Exibir
                  </Button>
                  : <Stack
                    direction="column"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Button
                      onClick={() => setShowManages({ open: false, id: '' })}
                      variant='text'
                      endIcon={<VisibilityOffIcon />}
                    >
                      Ocultar
                    </Button>
                    <Stack
                      direction="column"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                    >
                      {value && (results[1].data as any)?.users
                        ?.filter((elem: any) => value
                          ?.includes(elem.id))
                        ?.sort((a: any, b: any) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : - 1; })
                        ?.map((ele: any) => {
                          return <p>{ele.nome}</p>;
                        })}
                    </Stack>
                  </Stack>}
              </>}
            </>
          );
        },
        filter: false,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: 'equipes',
      label: 'Equipes',
      options: {
        customBodyRender: (value: any[], row: any) => {
          return (
            <>
              {value && <>
                {showTeams.id !== row.rowIndex
                  ? <Button
                    disabled={isLoading || isFetching}
                    onClick={() => { setShowTeams({ open: true, id: row.rowIndex }); }}
                    className="button is-light"
                    endIcon={<RemoveRedEyeIcon />}
                  >
                    Exibir
                  </Button>
                  : <Stack
                    direction="column"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Button
                      onClick={() => setShowTeams({ open: false, id: '' })}
                      variant='text'
                      endIcon={<VisibilityOffIcon />}
                    >
                      Ocultar
                    </Button>
                    <Stack
                      direction="column"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                    >
                      {value
                        ?.sort((a: any, b: any) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : - 1; })
                        ?.map((ele: any) => {
                          return <p>{ele.nome}</p>;
                        })}
                    </Stack>
                  </Stack>}
              </>}
            </>
          );
        },
      },
      filter: false,
      sort: true,
      sortThirdClickReset: true
    },
    {
      name: 'tiposDeServico',
      label: 'Tipos de Serviço',
      options: {
        customBodyRender: (value: any, row: any) => {
          return (
            <>
              {value && <>
                {showServices.id !== row.rowIndex
                  ? <Button
                    disabled={isLoading || isFetching}
                    onClick={() => setShowServices({ open: true, id: row.rowIndex })}
                    className="button is-light"
                    endIcon={<RemoveRedEyeIcon />}
                  >
                    Exibir
                  </Button>
                  : <Stack
                    direction="column"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Button
                      onClick={() => setShowServices({ open: false, id: '' })}
                      variant='text'
                      endIcon={<VisibilityOffIcon />}
                    >
                      Ocultar
                    </Button>
                    <Stack
                      direction="column"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                    >
                      {value
                        ?.sort((a: any, b: any) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : - 1; })
                        ?.map((ele: any) => {
                          return <p>{ele.nome}</p>;
                        })}
                    </Stack>
                  </Stack>}
              </>}
            </>
          );
        },
      },
      filter: false,
      sort: true,
      sortThirdClickReset: true
    },
    {
      name: "id",
      label: 'Editar/Ver',
      options: {
        customBodyRender: (value: any) => {

          return (
            <Button
              endIcon={<EditIcon />}
              onClick={() => {
                setSelectedManage((results[0].data as any[])?.find((el: manageType) => el.id === value));
                setOpen(true);
              }}
            >
              {(results[0].data as any[])?.find((el: manageType) => el.id === value)?.geral ? t('view') : t('edit')}
            </Button>
          );
        },
        filter: false,
        sort: false,
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  const getMuiTheme = () => createTheme({
    components: {
      MuiTable: {
        styleOverrides: {
          root: {
            marginTop: '20px',
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '3px',
            '&:nth-child(4)': {
              verticalAlign: 'top',
            },
            '&:nth-child(5)': {
              verticalAlign: 'top',
            },
            '&:nth-child(6)': {
              verticalAlign: 'top',
            }
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            textAlign: 'initial',
            padding: '5px'
          }
        }
      }
    }
  });

  if (results?.some((result: any) => result.isError)) {
    return <Typography>Erro ao buscar os dados</Typography>;
  }

  return (
    <>
      {open
        && <NewMgmtModal
          selectedManage={selectedManage}
          setSelectedManage={setSelectedManage}
          open={open}
          setOpen={setOpen}
          results={results}
        />}
      {(isLoading || isFetching) ?
        <Stack
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={2}
        >
          <Skeleton variant="rounded" height={35} width={180} />
          <Skeleton variant="rounded" height={400} width='100%' />
        </Stack>
        : <>
          <Button
          sx={{marginBottom: '10px'}}
            className="button is-success"
            variant='contained'
            endIcon={<AddCircleIcon />}
            onClick={() => setOpen(true)}
          >
            {t('Managements.addManageBtn').slice(0, -1)}
          </Button>
          <ThemeProvider theme={getMuiTheme()}>
            <MUIDataTable
              title={t('Managements.tabtitle')}
              data={results[0].data as any[]}
              columns={columns}
              options={{ ...tableOptions, ...translatedTextLabels }}
            />
          </ThemeProvider>
        </>}
    </>
  );
}
